import axios, { AxiosError, AxiosResponse } from "axios";

import { LogService, NjBreadcrumb, SearchService } from "@/services";
import { useBriefcaseStore } from "@/pinia/briefcase";
import { useSearchStore } from "@/pinia/search";
import {
  AdditionalRoleResponse,
  AppConfigConstants,
  AppConfigNestedNumberResponse,
  AppConfigNumberResponse,
  AppConfigResponse,
  Banner,
  BannerResponseItem,
  BriefcaseActions,
  BriefcaseFolder,
  BriefcaseFolderDownloadCheckResponse,
  BriefcaseFolderResponse,
  BriefcaseItem,
  BriefcaseResponse,
  BriefcaseResponseFolder,
  BriefcaseToggleRequest,
  CaucusCategory,
  CaucusCategoryResponse,
  CommitteeResponse,
  DateHelper,
  District,
  DistrictResponse,
  FecData,
  FecDataResponse,
  HomepageConfigResponse,
  LocationResponse,
  LookupTableItem,
  LookupTablesKeys,
  NestedLookupTableItem,
  NestedLookupTablesKeys,
  NonSuccessResponse,
  NoteworthyGroup,
  NoteworthyGroupResponseItem,
  Profile,
  ProfileAgencyResponse,
  ProfileDistrict,
  ProfileDistrictResponse,
  ProfileResponse,
  RedirectResponse,
  SalesforcePermission,
  SearchItem,
  SearchOrder,
  SearchResponse,
  SearchResponseItem,
  SearchResultsPagination,
  SearchRouteTypes,
  SearchSuggestionResponse,
  SearchSuggestions,
  SessionResponse,
  State,
  StateResponse,
  SubCommitteeResponse,
  SubscriptionModule,
  SubscriptionModuleResponse,
  User,
  UserContactResponse,
  UserType,
  VignetteEndpoints,
  Viz,
  VizCensusItem,
  VizCensusItemResponse,
  VizResponse,
} from "@/types";
import { BaseGroupResponse } from "@/types/api/BaseGroupResponse";
import {
  VignetteAgencyResponse,
  VignetteCaucusResponse,
  VignetteCommitteeResponse,
} from "@/types/api/SwaggerOutput";
import {
  BaseGroup,
  VignetteCaucus,
  VignetteCommittee,
  VignetteGroupMember,
} from "@/types/nj/Groups";
import { parseResponse } from "@/utils/TextCasingUtils";
import { useSessionStore } from "@/pinia/session";
import { useAppStore } from "@/pinia/app";
import { storeToRefs } from "pinia";
import { VignetteAgency } from "@/types/api/Agency";
import { CsvExportResponse } from "@/types/api/CsvExport";
import { StateCommitteeBucketResponse } from "@/types";
import { ParsedResponse } from "@/types/utils/CaseTypes";
import { TypedURLSearchParams } from "@/types/api/SearchEndpointInputs";
import { BulkDossierDownloadCheckResponse } from "@/types/api/DossierDownload";

// Used to help typing ApiResponses that have different error types
// See getProfile
type Result<Good, Bad extends Error> =
  | { ok: true; data: Good; error: null }
  | { ok: false; error: Bad; data: null };

/**
 * Singleton service that handles all API calls and updates Store
 * Please note that the use of searchStore in this file is not the recommended way of using the store.
 * It is only used here because the ApiService is immediately initialized before the store is.
 * Ideally an "ApiService" should not be using the store directly, but should return the results to the component
 * to handle updating the store there if necessary.
 */
export class ApiService {
  private static _initialized = false;

  static _initialize(): void {
    if (this._initialized) {
      LogService.error("ApiService is already initialized.");
      return;
    }
    LogService.debug("Initializing ApiService.");

    axios.defaults.headers.common = {
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
      Expires: "0",
    };

    // Global error interceptor - logs to console and captures server errors to Sentry
    axios.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        const status = error.response?.status;
        const method = error.config?.method?.toUpperCase();
        const url = error.config?.url;

        // Only capture server errors (5xx) or network failures to Sentry
        // 4xx errors are expected application behavior (auth failures, validation, etc.)
        if (!status || status >= 500) {
          LogService.error(
            `API request failed: ${method} ${url} - ${
              status || "Network Error"
            }`,
            error,
            {
              url,
              method,
              status: status || "Network Error",
              responseData: error.response?.data,
            }
          );
        }

        return Promise.reject(error);
      }
    );

    this._initialized = true;
    LogService.info("ApiService is initialized.");
  }

  /**
   * @param endpoint - the endpoint to be used for the request
   * @param parameter - the id of the object to be retrieved
   * @param queryParams - URLSearchParams
   */
  private static _getApiEndpoint(
    endpoint: VignetteEndpoints,
    parameter?: string,
    queryParams?: URLSearchParams | null
  ): string {
    const appStore = useAppStore();
    let queryStringParsed = "";
    let parameterParsed = "";

    if (parameter != null && parameter !== "") {
      parameterParsed = parameter + "/";
    }

    if (queryParams != null) {
      queryParams.delete("view");
      queryStringParsed = "?" + queryParams.toString();
    }

    const result: string =
      appStore.baseUrlPath + endpoint + parameterParsed + queryStringParsed;
    LogService.debug(`ApiEndpoint created: '${result}'`);

    return result;
  }

  static generateSearchExportUrl(endpoint: VignetteEndpoints): string {
    const searchStore = useSearchStore();
    const currentQuery = searchStore.search.currentSearchQuery;
    const queryParams: TypedURLSearchParams =
      SearchService.generateQuery(currentQuery);
    return this._getApiEndpoint(endpoint, undefined, queryParams);
  }

  static async getSearchExport(
    endpoint: VignetteEndpoints
  ): Promise<ParsedResponse<CsvExportResponse>> {
    const url = this.generateSearchExportUrl(endpoint);
    return axios
      .get<CsvExportResponse>(url)
      .then((res) => {
        return parseResponse<CsvExportResponse>(res.data);
      })
      .catch((error: AxiosError) => {
        return {
          errorMessage: error.toString(),
          almanacExportCount: 0,
          csvEncoded: "",
        };
      });
  }

  static async getNonSearchExport(
    endpoint: VignetteEndpoints
  ): Promise<ParsedResponse<CsvExportResponse>> {
    const url = this._getApiEndpoint(endpoint, undefined);
    return axios
      .get<CsvExportResponse>(url)
      .then((res) => {
        return parseResponse<CsvExportResponse>(res.data);
      })
      .catch((error: AxiosError) => {
        return {
          errorMessage: error.toString(),
          almanacExportCount: 0,
          csvEncoded: "",
        };
      });
  }

  static async addAllSearchResults(
    folderId: number,
    docType: string
  ): Promise<number[]> {
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.append("doc_type", docType);
    queryParams.append("folder_id", folderId.toString());
    return new Promise((resolve, reject) => {
      interface ErrorData {
        error_message: string;
        error_code: string;
      }

      axios
        .put(
          this._getApiEndpoint(
            VignetteEndpoints.API_ADD_ALL_SEARCH_RESULTS,
            undefined,
            queryParams
          )
        )
        .then((response: AxiosResponse<{ item_ids: number[] }>) => {
          resolve(response.data.item_ids);
        })
        .catch((error: AxiosError<ErrorData>) => {
          const errorData: ErrorData | null = error.response?.data || null;
          if (errorData === null) {
            reject("An error occurred, please try again.");
            return;
          }
          reject(errorData.error_message || "");
        });
    });
  }

  static async refreshFeaturedProfiles(forceUpdate = false): Promise<boolean> {
    const appStore = useAppStore();
    return new Promise<boolean>(async (resolve) => {
      const lastUpdated = appStore.getHomepageConfigLastUpdated;
      const isAlreadyLoading = appStore.isHomepageFeaturedProfilesLoading;
      const now = new Date();

      // Get new profiles only if it's been more than 5 minutes
      if (
        !isAlreadyLoading &&
        (forceUpdate ||
          lastUpdated == null ||
          now.getTime() - lastUpdated.getTime() > 300000)
      ) {
        LogService.info("Updating featured profiles.");

        // Clean up old profiles and refresh profiles on view load.
        appStore.clearHomePageProfilesAndGroups();
        appStore.homePageConfig.isLoading = true;

        await ApiService.getHomepageConfig();

        appStore.homePageConfig.isLoading = false;
        appStore.homePageConfig.lastUpdated = new Date();
      } else if (isAlreadyLoading) {
        LogService.info(`Skipping Featured Profiles update: isAlreadyLoading`);
      } else {
        LogService.info(
          `Skipping Featured Profiles update: lastUpdated: ${
            lastUpdated ? lastUpdated.getTime() : "null"
          }, diff: ${
            lastUpdated ? now.getTime() - lastUpdated.getTime() : "null"
          }`
        );
      }
      resolve(true);
    });
  }

  static async getHomepageConfig(): Promise<void> {
    const appStore = useAppStore();
    return new Promise<void>((resolve) => {
      axios
        .get<HomepageConfigResponse>(
          this._getApiEndpoint(VignetteEndpoints.API_HOMEPAGE_CONFIG)
        )
        .then((response: AxiosResponse<HomepageConfigResponse>) => {
          // Featured Profiles

          // featured_profiles_text

          if (
            !!response.data &&
            !!response.data.featured_profiles &&
            Array.isArray(response.data.featured_profiles)
          ) {
            appStore.homePageConfig.featuredProfilesText =
              response.data.featured_profiles_text;

            const featuredDistrict = response.data.featured_district;
            const featuredState = response.data.featured_state;
            const hasFeaturedStateOrDistrict =
              !!featuredDistrict || !!featuredState;
            const maxProfiles = hasFeaturedStateOrDistrict ? 4 : 8;

            for (const searchResponse of response.data.featured_profiles.slice(
              0,
              maxProfiles
            )) {
              const searchItem: SearchItem =
                ApiService.parseSearchResponseItem(searchResponse);
              if (searchItem != undefined)
                appStore.homePageConfig.featuredProfiles.push(searchItem);
            }
          } else {
            LogService.warn(
              `getHomepageConfig invalid featured profile list, endpoint returned: '${JSON.stringify(
                response.data
              )}'`
            );
          }

          // Recent Profiles
          if (
            !!response.data &&
            !!response.data.recent_profiles &&
            Array.isArray(response.data.recent_profiles)
          ) {
            for (const searchResponse of response.data.recent_profiles) {
              const searchItem: SearchItem =
                ApiService.parseSearchResponseItem(searchResponse);
              if (searchItem != undefined)
                appStore.homePageConfig.recentProfiles.push(searchItem);
            }
          } else {
            LogService.warn(
              `getHomepageConfig invalid recent profile list, endpoint returned: '${JSON.stringify(
                response.data
              )}'`
            );
          }

          // Noteworthy Groups
          if (
            !!response.data &&
            !!response.data.noteworthy_groups &&
            Array.isArray(response.data.noteworthy_groups)
          ) {
            for (const responseItem of response.data.noteworthy_groups) {
              const group: NoteworthyGroup =
                ApiService.parseNoteworthyGroupResponseItem(responseItem);
              if (group != undefined)
                appStore.homePageConfig.noteworthyGroups.push(group);
            }
          } else {
            LogService.warn(
              `getHomepageConfig invalid noteworthy groups list, endpoint returned: '${JSON.stringify(
                response.data
              )}'`
            );
          }

          if (!!response.data && !!response.data.primary_banner) {
            appStore.homePageConfig.primaryBanner =
              ApiService.parseBannerResponse(response.data.primary_banner);
          }

          if (!!response.data && !!response.data.secondary_banner) {
            appStore.homePageConfig.secondaryBanner =
              ApiService.parseBannerResponse(response.data.secondary_banner);
          }

          // Featured State
          if (!!response.data && !!response.data.featured_state) {
            appStore.homePageConfig.featuredState =
              ApiService.parseStateResponse(response.data.featured_state);
          }

          // Featured District
          if (!!response.data && !!response.data.featured_district) {
            appStore.homePageConfig.featuredDistrict =
              ApiService.parseDistrictResponse(response.data.featured_district);
          }
        })
        .catch(() => {
          // Error logged by axios interceptor
        })
        .then(() => {
          LogService.info("getHomepageConfig request complete.");
          resolve();
        });
    });
  }

  private static _getHidePayWalledProfilesApiValue(
    hidePayWalledProfilesQuery: boolean | undefined
  ): boolean {
    const sessionStore = useSessionStore();
    return (
      // Check if override is provided in search URL
      hidePayWalledProfilesQuery === true ||
      // fall back to user preference if URL override is not provided
      sessionStore.isHidePayWalledProfilesPreference
    );
  }

  /**
   * Helper function to change the "page" parameter to "- 1" as UI is 1-based but api is 0-based
   * @param page
   * @returns page - 1
   */
  static _checkPageParameterForSendingToApi(page: number): number {
    // Check if page (for whatever reason) is less than 1
    page = Math.max(1, page);
    return page - 1;
  }

  /**
   * Used to get search results for all search tabs
   * Since this function currently does a lot of other things besides getting search results from the api,
   * it makes sense to not re-invent the wheel and modify this function slightly to support multiple endpoints.
   * This requires that each endpoint used in this function return the same type of response as SearchResponse
   * @param endpoint
   */
  static async getVignetteSearch(endpoint: VignetteEndpoints): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const searchStore = useSearchStore();
      const currentQuery = searchStore.search.currentSearchQuery;

      const searchParams = SearchService.generateQuery(currentQuery);
      if (location.search.includes("only_kw")) {
        searchParams.set("only_kw", "true");
      }
      searchParams.set(
        "page",
        String(this._checkPageParameterForSendingToApi(currentQuery.page))
      );
      searchStore.setLastSearchQuery(currentQuery);
      searchStore.lastSearchString = searchParams.toString();

      axios
        .get(this._getApiEndpoint(endpoint, undefined, searchParams))
        .then((response: AxiosResponse<SearchResponse>) => {
          LogService.debug(`getVignetteSearch response is 200.`);
          ApiService.updateSearchResults(response.data);
        })
        .catch((error: AxiosError<SearchResponse>) => {
          const response = error.response;

          // Log errors - Network error / CORS issue / backend error
          LogService.addBreadcrumb({
            data: error.response?.data,
            category: "api",
            message: `response is ${response?.status || "null"}`,
            level: "error",
            type: "getVignetteSearch",
          });
          reject(response);
        })
        .then(() => {
          LogService.info("getVignetteSearch request complete.");
          resolve();
        });
    });
  }

  /**
   * This is used in the KeywordSearch component to retrieve keyword autocomplete search results for all
   * tabs in the search route
   * @param keyword - what the user is searching
   * @param endpoint - the endpoint to search
   * @param hidePayWalledProfiles - whether to hide pay-walled profiles
   */
  static async getAutocompleteSearchItems(
    keyword: string,
    endpoint: VignetteEndpoints,
    hidePayWalledProfiles: boolean | undefined
  ): Promise<SearchItem[]> {
    const queryParams: TypedURLSearchParams = new TypedURLSearchParams();

    return new Promise((resolve) => {
      queryParams.append("pageSize", "5");
      queryParams.append("q", keyword);
      queryParams.append("order", SearchOrder.RELEVANCE);
      queryParams.append("autocomplete", "true");

      if (this._getHidePayWalledProfilesApiValue(hidePayWalledProfiles)) {
        queryParams.append("hide_paywalled_profiles", "true");
      }

      axios
        .get(this._getApiEndpoint(endpoint, undefined, queryParams))
        .then((response: AxiosResponse<SearchResponse>) => {
          LogService.debug(`getAutoCompleteSearch response is 200.`);
          resolve(this.parseSearchResponseList(response.data.results));
        })
        .catch(() => {
          // Error logged by axios interceptor
        });
    });
  }

  private static parseSearchResponseItem(
    searchResponseItem: SearchResponseItem
  ): SearchItem {
    const briefcaseStore = useBriefcaseStore();
    return {
      subscriptionModules: searchResponseItem?.subscription_modules || [],
      id: searchResponseItem.id || -1,
      briefcaseItemId: searchResponseItem.briefcase_item_id || -1,
      isFormer: searchResponseItem.is_former || false,
      positionSummary: searchResponseItem.position_summary || "",
      preferredNameDisplay: searchResponseItem.preferred_name_display || "",
      title: searchResponseItem.title || "",
      politicalParty: searchResponseItem.political_party || "",
      quickSummary: searchResponseItem.quick_summary || "",
      headshot: searchResponseItem.headshot || "",
      addedToBriefcase: briefcaseStore.isAddedToBriefcaseById(
        searchResponseItem.briefcase_item_id || -1
      ),
      recordType: searchResponseItem?.record_type ?? searchResponseItem.type,
    };
  }

  private static parseNoteworthyGroupResponseItem(
    groupResponseItem: NoteworthyGroupResponseItem
  ): NoteworthyGroup {
    return {
      id: groupResponseItem.id || -1,
      name: groupResponseItem.name || "",
      description: groupResponseItem.description || "",
      order: groupResponseItem.order || Number.MAX_SAFE_INTEGER,
    };
  }

  private static parseBannerResponse(banner: BannerResponseItem): Banner {
    return {
      wideImg: banner.wide_img || "",
      smallImg: banner.small_img || "",
      url: banner.url || "",
    };
  }

  private static parseVizCensusItemResponse(
    vizCensusItemResponse: VizCensusItemResponse[] | null
  ): VizCensusItem[] {
    if (vizCensusItemResponse == null) {
      return [];
    }
    const returnData = [];
    for (const data of vizCensusItemResponse) {
      returnData.push({
        category: data.category || "",
        displayTitle: data.display_title || "",
        title: data.title || "",
        value: data.value || "",
      });
    }
    return returnData;
  }

  private static parseVizResponse(vizresp: VizResponse[] | null): Viz[] {
    if (vizresp == null) {
      return [];
    }
    const returnData = [];
    for (const data of vizresp) {
      returnData.push({
        label: data.label || "",
        censusItems: ApiService.parseVizCensusItemResponse(data.census_items),
      });
    }
    return returnData;
  }

  private static parseSearchResponseList(
    searchResponseList: SearchResponseItem[] | null
  ): SearchItem[] {
    if (searchResponseList == null) {
      return [];
    } else {
      return searchResponseList.map((searchResponse) => {
        return this.parseSearchResponseItem(searchResponse);
      });
    }
  }

  // TODO: validate response?
  private static updateSearchResults(response: SearchResponse): void {
    const results: SearchItem[] = this.parseSearchResponseList(
      response.results
    );
    const searchStore = useSearchStore();
    const searchPagination: SearchResultsPagination = {
      pageSize: response.pagination?.pageSize || 12,
      page:
        response.pagination?.page != null ? response.pagination.page + 1 : 1,
      totalPages:
        response.pagination?.totalPages != null
          ? response.pagination.totalPages
          : 1,
      totalHits: response.pagination?.totalHits || 0,
      order: response.pagination?.order || SearchOrder.RELEVANCE,
    };

    searchStore.setSearchResultsAndPagination(searchPagination, results);
  }

  // TODO: validate profileId
  /**
   * Returns a Person profile from the API.
   * @param profileId - the ID of the person
   * @param isKnowWho - if the ID is a KnowWho ID
   */
  static async getProfile(
    profileId: number,
    isKnowWho: boolean
  ): Promise<Result<Profile, NonSuccessResponse>> {
    let result: Profile | null = null;
    const endpoint = isKnowWho
      ? VignetteEndpoints.API_PROFILE_KW
      : VignetteEndpoints.API_PROFILE;

    return new Promise((resolve) => {
      axios
        .get<ProfileResponse>(this._getApiEndpoint(endpoint, String(profileId)))
        .then((response: AxiosResponse<ProfileResponse>) => {
          result = ApiService.parseProfileResponse(response.data);
          resolve({ ok: true, data: result, error: null });
        })
        .catch((error: AxiosError<{ url: string }>) => {
          const response = error.response;
          const status = response?.status;
          if (status) {
            if (status === 303) {
              const url: string = error.response?.data?.url ?? "";
              const err = new RedirectResponse(url);
              resolve({
                ok: false,
                data: null,
                error: err,
              });
              return;
            }
          }

          // Log errors - Network error / CORS issue / backend error
          const breadcrumb: NjBreadcrumb = {
            data: error.response?.data,
            category: "api",
            message: `response is ${status || "null"}`,
            level: "error",
            type: "getProfile",
          };
          LogService.addBreadcrumb(breadcrumb);

          resolve({
            ok: false,
            error: new NonSuccessResponse(error.message, status ?? ""),
            data: null,
          });
        })
        .then(() => {
          LogService.info("getProfile request complete.");
        });
    });
  }

  static async getCommittee(committeeId: number): Promise<VignetteCommittee> {
    let result: VignetteCommittee | null = null;
    const endpoint = VignetteEndpoints.API_COMMITTEE;

    return new Promise((resolve, reject) => {
      axios
        .get(this._getApiEndpoint(endpoint, String(committeeId)))
        .then((response: AxiosResponse<VignetteCommitteeResponse>) => {
          result = ApiService.parseCommitteeResponse(response.data);
          resolve(result);
        })
        .catch((error: AxiosError<VignetteCommitteeResponse>) => {
          reject(error.response);
        })
        .finally(() => {
          LogService.info("getCommittee request complete.");
        });
    });
  }

  static async getState(stateId: string | number): Promise<State> {
    let result: State | null = null;
    const endpoint = VignetteEndpoints.API_STATE;

    return new Promise((resolve, reject) => {
      axios
        .get(this._getApiEndpoint(endpoint, String(stateId)))
        .then((response: AxiosResponse<StateResponse>) => {
          result = ApiService.parseStateResponse(response.data);
          resolve(result);
        })
        .catch((error: AxiosError<StateResponse>) => {
          reject(error.response);
        })
        .finally(() => {
          LogService.info("getState request complete.");
        });
    });
  }

  static parseStateResponse(response: StateResponse): State {
    const briefcaseStore = useBriefcaseStore();

    const parseDistrict = (d: ProfileDistrictResponse): ProfileDistrict => {
      let number = d.number.replace(/^0/, "");
      if (d.name.includes("At-Large")) number = "AL";
      return {
        id: d.id,
        number: number,
        name: d.name,
        legDistrictId: d.legdistrictid,
      };
    };

    return {
      addedToBriefcase: briefcaseStore.isAddedToBriefcaseById(
        response.briefcase_item_id
      ),
      briefcaseItemId: response.briefcase_item_id,
      censusViz: ApiService.parseVizResponse(response.census_viz),
      econViz: ApiService.parseVizResponse(response.econ_viz),
      geodataSimple: response.geodata_simple,
      governor: response.governor
        ? ApiService.parseSearchResponseItem(response.governor)
        : null,
      id: response.id,
      name: response.name,
      narrative: response.narrative,
      presPolitics: response.pres_politics,
      presElectionResults: response.pres_election_results.map((p) => {
        return {
          results: p.results.map((r) => ({
            election: r.election,
            name: r.name,
            percentageCleaned: r.percentage_cleaned,
            votes: r.votes,
          })),
          title: p.title,
        };
      }),
      racetrackerElectionUrl: response.racetracker_election_url,
      representatives:
        response.representatives?.map((r) => ({
          ...ApiService.parseSearchResponseItem(r),
          district: parseDistrict(r.district),
        })) ?? [],
      senators: response.senators.map((s) =>
        ApiService.parseSearchResponseItem(s)
      ),
      censusData: response.census_data.map((s) => ({
        ...s,
        displayCategory: s.display_category,
        displayTitle: s.display_title,
      })),
      stateRepresentatives:
        response.state_representatives?.map((r) => ({
          ...ApiService.parseSearchResponseItem(r),
          district: parseDistrict(r.district),
        })) ?? [],
      stateSenators:
        response.state_senators?.map((s) => ({
          ...ApiService.parseSearchResponseItem(s),
          district: parseDistrict(s.district),
        })) ?? [],
    };
  }

  static async getDistrict(districtId: string | number): Promise<District> {
    let result: District | null = null;
    const endpoint = VignetteEndpoints.API_DISTRICT;

    return new Promise((resolve, reject) => {
      axios
        .get(this._getApiEndpoint(endpoint, String(districtId)))
        .then((response: AxiosResponse<DistrictResponse>) => {
          result = ApiService.parseDistrictResponse(response.data);
          resolve(result);
        })
        .catch((error: AxiosError<District>) => {
          // Error logged by axios interceptor
          reject(error.response);
        })
        .finally(() => {
          LogService.info("getDistrict request complete.");
        });
    });
  }

  static parseDistrictResponse(response: DistrictResponse): District {
    const briefcaseStore = useBriefcaseStore();
    return {
      addedToBriefcase: briefcaseStore.isAddedToBriefcaseById(
        response.briefcase_item_id
      ),
      chips: response.chips ?? [],
      narrative: response.narrative,
      briefcaseItemId: response.briefcase_item_id,
      censusViz: ApiService.parseVizResponse(response.census_viz),
      econViz: ApiService.parseVizResponse(response.econ_viz),
      censusData: response.census_data.map((s) => ({
        ...s,
        displayCategory: s.display_category,
        displayTitle: s.display_title,
      })),
      id: response.id,
      name: response.name,
      legChamberId: response.legchamberid,
      legDistrictId: response.legdistrictid,
      number: response.number,
      geodataSimple: response.geodata_simple,
      geographicLabel: response?.geographic_label ?? "",
      racetrackerElectionUrl: response.racetracker_election_url,
      representatives: response.representatives.map((r) => {
        return ApiService.parseSearchResponseItem(r);
      }),
      state: {
        id: response.state.id,
        briefcaseItemId: response.state.briefcase_item_id,
        name: response.state.name,
      },
      presElectionResults: response.pres_election_results.map((p) => {
        return {
          results: p.results.map((r) => ({
            election: r.election,
            name: r.name,
            percentageCleaned: r.percentage_cleaned,
            votes: r.votes,
          })),
          title: p.title,
        };
      }),
      congressElectionResults: response.congress_election_results.map((p) => {
        return {
          results: p.results.map((r) => ({
            election: r.election,
            name: r.name,
            percentageCleaned: r.percentage_cleaned,
            votes: r.votes,
          })),
          title: p.title,
        };
      }),
      title: response.title,
    };
  }

  static async getCaucus(caucusId: number): Promise<VignetteCaucus> {
    let result: VignetteCaucus | null = null;
    const endpoint = VignetteEndpoints.API_CAUCUS;

    return new Promise((resolve, reject) => {
      axios
        .get(this._getApiEndpoint(endpoint, String(caucusId)))
        .then((response: AxiosResponse<VignetteCaucusResponse>) => {
          result = ApiService.parseCaucusResponse(response.data);
          resolve(result);
        })
        .catch((error: AxiosError<VignetteCaucusResponse>) => {
          reject(error.response);
        })
        .finally(() => {
          LogService.info("getCaucus request complete.");
        });
    });
  }

  static async getAgency(agencyId: string | number): Promise<VignetteAgency> {
    let result: VignetteAgency | null = null;
    const endpoint = VignetteEndpoints.API_AGENCY;

    return new Promise((resolve, reject) => {
      axios
        .get(this._getApiEndpoint(endpoint, String(agencyId)))
        .then((response: AxiosResponse<VignetteAgencyResponse>) => {
          result = ApiService.parseAgencyResponse(response.data);
          resolve(result);
        })
        .catch((error: AxiosError<VignetteAgencyResponse>) => {
          reject(error.response);
        })
        .finally(() => {
          LogService.info("getAgency request complete.");
        });
    });
  }

  private static parseAgencyResponse(
    response: VignetteAgencyResponse
  ): VignetteAgency {
    const briefcaseStore = useBriefcaseStore();
    return {
      addedToBriefcase: briefcaseStore.isAddedToBriefcaseById(
        response.briefcase_item_id
      ),
      address: response.address,
      agencyFull: response.agency_full,
      briefcaseItemId: response.briefcase_item_id,
      children:
        response.children?.map((c) => ({
          id: c.id,
          briefcaseItemId: c.briefcase_item_id,
          logo: c.logo,
          role: c.role,
          name: c.name,
        })) ?? [],
      chips: response.chips ?? [],
      email: response.email,
      fax: response.fax,
      id: response.id,
      logo: response.logo,
      name: response.name,
      parent: response.parent
        ? {
            id: response.parent?.id ?? 0,
            briefcaseItemId: response.parent?.briefcase_item_id ?? 0,
            logo: response.parent?.logo ?? "",
            role: response.parent?.role ?? "",
            name: response.parent?.name ?? "",
          }
        : null,
      people: response.people.map(parseResponse),
      phone: response.phone,
      role: response.role,
      website: response.website,
    };
  }

  private static parseLocationResponseArray(
    responseArray: LocationResponse[] | null
  ): string[] {
    return (responseArray ?? [])
      .map((locationResponse) => this.parseLocationResponse(locationResponse))
      .filter(Boolean);
  }

  private static parseLocationResponse(
    response: LocationResponse | null
  ): string {
    return (response?.display ?? "")
      .replace(/, United States of America$/, "")
      .trim();
  }

  private static parseFecDataResponse(
    response: FecDataResponse | null
  ): FecData | null {
    return response && response.cid
      ? {
          cashOnHand: parseFloat(response.cash_on_hand),
          cid: response.cid,
          cycle: parseInt(response.cycle),
          debt: parseFloat(response.debt),
          lastUpdated: response.last_updated, // This could be a date object...
          spent: parseFloat(response.spent),
          total: parseFloat(response.total),
        }
      : null;
  }

  private static parseFieldFromLookupTable(
    field: string,
    lookupTable: LookupTableItem[]
  ): string {
    if (field && lookupTable != null && lookupTable.length > 0) {
      const fieldParsed = lookupTable.find((entry) => entry.id === field);
      if (fieldParsed) {
        return fieldParsed.name;
      }
    }
    return field;
  }

  /**
   * If only the year is set, just display the year
   * If only the month and birthdate_year are set, display them as MMMM YYYY
   * If only the month and birthdate_day are set, display them as MMMM d
   * If all date components are set, display the date as MMMM d, YYYY
   * If it's birthday and year is set, after the birthdate display the age: (age 53)
   */
  private static parseDate(
    year: number | null,
    month: number | null,
    day: number | null,
    age: string | null = null
  ): string {
    // Check if we have any valid combination available.
    if (!year && !month && (!day || !year)) {
      LogService.debug(
        `ApiService: can't parse birthday: year:'${year}', month:'${month}', day:'${day}'`
      );
      return "";
    }

    const yearAndAge = year ? year + (age ? ` (age ${age})` : "") : "";

    if (!month) {
      return yearAndAge;
    } else {
      if (day) {
        return `${DateHelper.getMonthString(month)} ${day}${
          yearAndAge ? ", " + yearAndAge : ""
        }`;
      } else {
        return DateHelper.getMonthString(month) + " " + yearAndAge;
      }
    }
  }

  private static getDateObject(isoString: string): Date {
    return new Date(isoString);
  }

  private static parseIsoDate(dateString: string | null): string {
    if (!!dateString) {
      try {
        const date = this.getDateObject(dateString);
        return this.parseDate(
          date.getFullYear(),
          date.getMonth() + 1,
          date.getDate()
        );
      } catch (e) {}
    }
    return "";
  }

  private static parseProfileResponse(response: ProfileResponse): Profile {
    const briefcaseStore = useBriefcaseStore();
    const appStore = useAppStore();
    /**
     *api.A helper function for mapping caucuses received from API
     * @param caucuses - a map
     * @returns CaucusCategory[]
     */
    const caucusMapper = (
      caucuses: CaucusCategoryResponse[]
    ): CaucusCategory[] => {
      if (!caucuses) return [];
      return caucuses.map((category) => {
        return {
          caucusCategoryName: category.category_name ?? "",
          caucuses:
            category.caucuses?.map((caucus) => {
              return {
                id: caucus.id ?? 0,
                caucusName: caucus.caucus_name ?? "",
                leadershipTitle: caucus.leadership_title ?? "",
              };
            }) ?? [],
        };
      });
    };

    const formatDateTime = (
      options: Intl.DateTimeFormatOptions,
      datetime?: string
    ): string => {
      if (!datetime || !options) return "";
      return new Date(datetime).toLocaleDateString("default", options);
    };

    const getCommitteeLeadership = (): { name: string; position: string }[] => {
      const committees = response?.committees ?? [];
      const roles = [];
      for (const c of committees) {
        let title = c.leadership_title;
        if (title) {
          const isChairman = title.toLowerCase().indexOf("chairman") > -1;
          const isFemale = response.gender?.toLowerCase() === "female";
          if (isChairman && isFemale) {
            title = title.replace("Chairman", "Chairwoman");
          }
          roles.push({ name: c.committee_name, position: title });
        }
      }
      return roles;
    };

    return {
      chips: response.chips ?? [],
      racetrackerCandidateUrl: response.racetracker_candidate_url || "",
      racetrackerElectionUrl: response.racetracker_election_url || "",
      electionStatus: response.election_status
        ? {
            name: response.election_status.race_name,
            status: response.election_status.race_status,
            year: response.election_status.race_year,
          }
        : null,
      additionalRoles: (response.additional_roles ?? []).map(
        parseResponse<AdditionalRoleResponse>
      ),
      agency: response.agency
        ? parseResponse<ProfileAgencyResponse>(response.agency)
        : null,
      knowWhoId: response.knowwho_id + "",
      leadershipPost: response.leadership_post ?? "",
      committeeLeadership: getCommitteeLeadership(),
      firstElected: formatDateTime(
        { month: "long", day: "numeric", year: "numeric" },
        response.first_elected_to_seat
      ),
      nextElectionDate: formatDateTime(
        { month: "long", year: "numeric" },
        response.next_election_date
      ),
      numberTenuresInSeat: response.number_tenures_in_seat ?? 0,
      priorWinPct: response.prior_winning_percent ?? "",
      state: {
        name: response.state?.name ?? "",
        id: response.state?.id?.toString() ?? "",
      },
      district: {
        name: response.district?.name ?? "",
        id: response.district?.id?.toString() ?? "",
        number: response.district?.number?.toString() ?? "",
        legDistrictId: response.district?.legdistrictid?.toString() ?? "",
      },
      youtube: response.youtube ?? "",
      headshot: response.headshot || "",
      preferredNameDisplay: response.preferred_name_display || "",
      positionSummary: response.position_summary || "",
      phoneticName: response.phonetic_name || "",
      primaryProfileType: this.parseFieldFromLookupTable(
        response.primary_profile_type || "",
        appStore.getLookupTable(LookupTablesKeys.PRIMARY_PROFILE_TYPES)
      ),
      staffers: response.staffers?.map(parseResponse) ?? [],
      legislativeInfoHeading: response.legislative_info_heading,
      staffersHeading: response.staffers_heading,
      offices: response.offices?.map(parseResponse) ?? [],
      // adding a "padding-bottom" style here because on the profile page it feels squished together
      fullNarrative:
        response.full_narrative?.replaceAll(
          "<p>",
          `<p style="padding-bottom: 10px;">`
        ) ?? "",
      primaryProfileTypesAdditional: (
        response.primary_profile_types_additional ?? []
      ).map((profileType) =>
        this.parseFieldFromLookupTable(
          profileType || "",
          appStore.getLookupTable(LookupTablesKeys.PRIMARY_PROFILE_TYPES)
        )
      ),
      secondaryProfileTypes: (response.secondary_profile_types ?? []).map(
        (profileType) =>
          this.parseFieldFromLookupTable(
            profileType || "",
            appStore.getLookupTable(LookupTablesKeys.SECONDARY_PROFILE_TYPES)
          )
      ),
      gender: response.gender || "",
      age: response.age || "",
      birthplace: this.parseLocationResponse(response.birthplace),
      hometown: this.parseLocationResponse(response.hometown),
      residences: this.parseLocationResponseArray(response.residences),
      religion: this.parseFieldFromLookupTable(
        response.religion || "",
        appStore.getLookupTable(LookupTablesKeys.RELIGIONS)
      ),
      ethnicity: this.parseFieldFromLookupTable(
        response.ethnicity || "",
        appStore.getLookupTable(LookupTablesKeys.ETHNICITIES)
      ),
      education: (response.education ?? []).map((education) => {
        return {
          degree: this.parseFieldFromLookupTable(
            education.degree || "",
            appStore.getLookupTable(LookupTablesKeys.DEGREES)
          ),
          degreeIncomplete: education.degree_incomplete || false,
          fieldOfStudy: education.field_of_study || "",
          school: !education.school
            ? {
                name: "",
                type: "",
                location: this.parseLocationResponse(null),
              }
            : {
                name: education.school.name || "",
                type: education.school.type || "",
                location: this.parseLocationResponse(education.school.location),
              },
          school_department: education.school_department || "",
          yearStart: education.year_start || -1,
          yearEnd: education.year_end || -1,
        };
      }),
      educationSummary: response.education_summary || "", // HTML
      maritalStatus: this.parseFieldFromLookupTable(
        response.marital_status || "",
        appStore.getLookupTable(LookupTablesKeys.MARITAL_STATUSES)
      ),
      familyMembers: response.family_members || "", // HTML
      title: response.title || "",
      politicalPartyFull: this.parseFieldFromLookupTable(
        response.political_party || response.political_party_other || "",
        appStore.getLookupTable(LookupTablesKeys.POLITICAL_PARTIES)
      ),
      politicalParty: response.political_party || "",
      electoralHistory:
        (response.electoral_history ?? []).map((electoralHistory) => {
          return {
            year: electoralHistory.year || "",
            raceName: electoralHistory.race_name || "",
            status: electoralHistory.status || "",
          };
        }) ?? [],
      workHistory: response.work_history || "", // HTML
      electoralOverview: response.electoral_overview || "", // HTML
      committees: (response.committees ?? []).map(
        (committee: CommitteeResponse) => {
          return {
            committeeName: committee.committee_name || "",
            leadershipTitle: committee.leadership_title || "",
            id: committee.id || 0,
            subCommittees: (committee.sub_committees ?? []).map(
              (subCommittee: SubCommitteeResponse) => {
                return {
                  id: subCommittee.id || 0,
                  committeeName: subCommittee.committee_name || "",
                  leadershipTitle: subCommittee.leadership_title || "",
                };
              }
            ),
          };
        }
      ),
      caucuses: caucusMapper(response.caucuses),
      allCaucuses: caucusMapper(response.all_caucuses),
      quickSummary: response.quick_summary || "", // HTML
      approachMotivations: response.approach_motivations || "", // HTML
      policyPositionsFocus: response.policy_positions_focus || "", // HTML
      coreCommunities: response.core_communities || "", // HTML
      criticismsControversies:
        (response.criticisms_controversies as string) || "", // HTML
      financialInfo: response.financial_info || "", // HTML
      professionalAffiliations: response.professional_affiliations || "", // HTML
      facebookUrl: response.facebook_url || "",
      twitterUsername: response.twitter_username || "",
      instagramUsername: response.instagram_username || "",
      linkedInUrl: response.linkedin_url || "",
      websiteUrl: response.website_url || "",
      almanacUrl: response.almanac_url || "",
      address: response.address || "",
      email: response.email || "",
      phone: response.phone || "",
      fecData: this.parseFecDataResponse(response.fec_data),
      keyVotes: response.key_votes.map((keyVote) => {
        return {
          billNumber: keyVote.bill_number,
          name: keyVote.name,
          vote: keyVote.vote,
        };
      }),

      sigRatings: response.sig_ratings.map((sigRating) => {
        return {
          name: sigRating.special_interest_group__name,
          rating:
            sigRating.rating +
            (Number.isInteger(Number.parseInt(sigRating.rating)) ? "%" : ""),
          timespan: sigRating.timespan,
        };
      }),

      relatedProfiles: response.related_profiles.map((relatedProfile) => {
        return {
          subscriptionModules: relatedProfile.subscription_modules || [],
          id: relatedProfile.id || -1,
          briefcaseItemId: relatedProfile.briefcase_item_id || -1,
          isFormer: relatedProfile.is_former || false,
          positionSummary: relatedProfile.position_summary || "",
          preferredNameDisplay: relatedProfile.preferred_name_display || "",
          title: relatedProfile.title || "",
          politicalParty: relatedProfile.political_party || "",
          quickSummary: relatedProfile.quick_summary || "",
          headshot: relatedProfile.headshot || "",
          /**
           * @deprecated potentially
           */
          addedToBriefcase: briefcaseStore.isAddedToBriefcaseById(
            relatedProfile.briefcase_item_id || -1
          ),
          recordType: relatedProfile.record_type,
        };
      }),
      presentationCenterUrl: response.related_document_default_url || "",

      id: response.id || 0,
      briefcaseItemId: response.briefcase_item_id || 0,
      docxUrl: response.docx_url || "",
      pdfUrl: response.pdf_url || "",
      createdDate: response.created_date
        ? new Date(response.created_date)
        : null, // ISO-Date
      isStub: response.is_stub || false,
      isFormer: response.is_former || false,
      subscriptionModules: response.subscription_modules || [],
      isUnlocked: response.is_unlocked || false,

      /* computed */
      birthDate: this.parseDate(
        response.birthdate_year,
        response.birthdate_month,
        response.birthdate_day,
        response.age || ""
      ),
      updatedDate: this.parseIsoDate(response.updated_date),
      substantiveUpdatedDate: this.parseIsoDate(
        response.substantive_updated_date
      ),
      /**
       * @deprecated potentially
       */
      addedToBriefcase: briefcaseStore.isAddedToBriefcaseById(
        response.briefcase_item_id || -1
      ), // computed
      recordType: response.record_type,
    };
  }

  private static parseCommitteeResponse(
    response: VignetteCommitteeResponse
  ): VignetteCommittee {
    const briefcaseStore = useBriefcaseStore();
    const addBriefcaseStatus = (
      items: VignetteGroupMember[]
    ): VignetteGroupMember[] =>
      items.map(
        (item) =>
          ({
            ...item,
            addedToBriefcase: briefcaseStore.isAddedToBriefcaseById(
              item.briefcaseItemId as number
            ),
          } as VignetteGroupMember)
      );
    return {
      ...ApiService.parseBaseGroupResponse(response),
      parent: response.parent ? parseResponse(response.parent) : null,
      chips: response.chips ?? [],
      members: {
        democratic: addBriefcaseStatus(
          response.members?.democratic?.map(parseResponse) ?? []
        ),
        republican: addBriefcaseStatus(
          response.members?.republican?.map(parseResponse) ?? []
        ),
        independent: addBriefcaseStatus(
          response.members?.independent?.map(parseResponse) ?? []
        ),
      },
      presentationCenterUrl: response.related_document_default_url || "",
      staffers: response.staffers.map(parseResponse),
      subcommittees: response.subcommittees?.map(parseResponse) ?? [],
    };
  }

  private static parseCaucusResponse(
    response: VignetteCaucusResponse
  ): VignetteCaucus {
    const briefcaseStore = useBriefcaseStore();
    const addBriefcaseStatus = (
      items: Omit<VignetteGroupMember, "addedToBriefcase">[]
    ): VignetteGroupMember[] =>
      items.map(
        (item) =>
          ({
            ...item,
            addedToBriefcase: briefcaseStore.isAddedToBriefcaseById(
              item.briefcaseItemId as number
            ),
          } as VignetteGroupMember)
      );

    return {
      ...ApiService.parseBaseGroupResponse(response),
      parent: response.parent ? parseResponse(response.parent) : null,
      members: {
        democratic: addBriefcaseStatus(
          response.members?.democratic?.map(parseResponse) ?? []
        ),
        republican: addBriefcaseStatus(
          response.members?.republican?.map(parseResponse) ?? []
        ),
        independent: addBriefcaseStatus(
          response.members?.independent?.map(parseResponse) ?? []
        ),
      },
      children: response.children?.map(parseResponse) ?? [],
    };
  }

  private static parseBaseGroupResponse(
    response: BaseGroupResponse
  ): BaseGroup {
    const briefcaseStore = useBriefcaseStore();

    return {
      ...parseResponse(response),
      updatedDate: this.parseIsoDate(response.updated_date),
      addedToBriefcase: briefcaseStore.isAddedToBriefcaseById(
        response.briefcase_item_id || -1
      ),
    };
  }

  static async getJobTitlePhrase(
    search: string,
    type: "federal" | "congressional" | "state"
  ): Promise<string[] | undefined> {
    const queryParams = new URLSearchParams();
    queryParams.append("search", search);
    queryParams.append("doctype", type);
    const endpoint = this._getApiEndpoint(
      VignetteEndpoints.API_JOB_TITLE_SUGGEST_KW,
      undefined,
      queryParams
    );

    try {
      const response: AxiosResponse<string[]> = await axios.get(endpoint);
      if (response.data != null) {
        return response.data;
      }
    } catch {
      return [];
    }
  }

  // Returns true if there is a next page.
  private static async getBriefcase(pageNumber = 1): Promise<boolean> {
    const briefcaseStore = useBriefcaseStore();
    const queryParams = new URLSearchParams();
    queryParams.append("page", String(pageNumber));

    try {
      const response: AxiosResponse<BriefcaseResponse> = await axios.get(
        this._getApiEndpoint(
          VignetteEndpoints.API_BRIEFCASE,
          undefined,
          queryParams
        )
      );
      if (response.data?.results != null) {
        const briefcaseFolders: BriefcaseFolder[] = response.data.results.map(
          (briefcaseResponseFolder) => {
            return ApiService.parseBriefcaseResponseFolder(
              briefcaseResponseFolder
            );
          }
        );

        briefcaseStore.setBriefcaseFolders(briefcaseFolders);

        // return response.data.next != null && response.data.next != "";
      }
    } catch {
      // Error logged by axios interceptor
    }
    return false;
  }

  // Refresh briefcase if more than 2 minutes has passed (or forced by flag), skip if refreshed more recently.
  static async refreshBriefcase(forceUpdate = false): Promise<boolean> {
    return new Promise<boolean>(async (resolve) => {
      const sessionStore = useSessionStore();
      if (!sessionStore.hasVignetteAccess) {
        resolve(true);
        return;
      }
      const briefcaseStore = useBriefcaseStore();
      const lastUpdated = briefcaseStore.meta.lastUpdated;
      const isAlreadyLoading = briefcaseStore.meta.isLoading;
      const now = new Date();

      // Update briefcase only if it's been more than 2 minutes
      if (
        !isAlreadyLoading &&
        (forceUpdate ||
          lastUpdated == null ||
          now.getTime() - lastUpdated.getTime() > 120000)
      ) {
        LogService.info("Updating briefcase items.");

        briefcaseStore.meta.isLoading = true;

        await this.getBriefcase();

        briefcaseStore.meta.isLoading = false;
        briefcaseStore.meta.lastUpdated = new Date();
      } else if (isAlreadyLoading) {
        LogService.info(`Skipping Briefcase update: isAlreadyLoading`);
      } else {
        LogService.info(
          `Skipping Briefcase update: lastUpdated: ${
            lastUpdated ? lastUpdated.getTime() : "null"
          }, diff: ${
            lastUpdated ? now.getTime() - lastUpdated.getTime() : "null"
          }`
        );
      }
      resolve(true);
    });
  }

  private static parseBriefcaseResponseFolder(
    briefcaseResponseFolder: BriefcaseResponseFolder
  ): BriefcaseFolder {
    return {
      id: briefcaseResponseFolder.id || -1,
      isDefault: briefcaseResponseFolder.is_default || false,
      name: briefcaseResponseFolder.name || "General Briefcase",
      items:
        briefcaseResponseFolder.items?.map((item) => {
          return ApiService.parseSearchResponseItem(item);
        }) ?? [],
      dossierUrl: briefcaseResponseFolder.dossier_url || "#",
    };
  }

  static async postBriefcaseToggle(
    items: SearchItem[],
    action?: BriefcaseActions,
    sourceFolderId?: number,
    destinationFolderId?: number
  ): Promise<boolean> {
    const briefcaseStore = useBriefcaseStore();

    if (
      action !== undefined &&
      action !== BriefcaseActions.ADD &&
      action !== BriefcaseActions.REMOVE &&
      action !== BriefcaseActions.MOVE
    ) {
      LogService.warn(
        `ApiService.postBriefcaseToggle invalid action: '${action}`
      );
      return false;
    }

    if (items.length === 0) {
      LogService.warn("ApiService.postBriefcaseToggle profile list is empty.");
      return false;
    }
    if (sourceFolderId === undefined) {
      if (action === BriefcaseActions.ADD) {
        sourceFolderId = briefcaseStore.meta.defaultFolderId;
      } else {
        // action === BriefcaseActions.REMOVE || action === BriefcaseActions.MOVE
        sourceFolderId = briefcaseStore.getFolderIdById(
          items[0]?.briefcaseItemId ?? -1
        ).value;

        if (sourceFolderId < 0) {
          LogService.warn(
            "ApiService.postBriefcaseToggle items aren't added to a briefcase folder."
          );
          return false;
        }
      }
    }

    if (action === BriefcaseActions.MOVE) {
      // This if check allows us to use a non null assertion below
      if (destinationFolderId === undefined) {
        LogService.warn(
          `ApiService.postBriefcaseToggle destination folder needed for move operation.`
        );
        return false;
      }

      if (sourceFolderId === destinationFolderId) {
        LogService.warn(
          `ApiService.postBriefcaseToggle source and destination folder cannot be same.`
        );
        return false;
      }
    }
    // FIXME: check for error message
    const result = await ApiService.postBriefcaseToggleWithId(
      items.map((searchItem) => searchItem.briefcaseItemId),
      action,
      sourceFolderId,
      destinationFolderId
    );
    if (result) {
      if (action === BriefcaseActions.ADD) {
        briefcaseStore.addToBriefcase({
          items,
          folderId: sourceFolderId,
        });
      } else if (action === BriefcaseActions.REMOVE) {
        briefcaseStore.removeFromBriefcase({
          items,
          folderId: sourceFolderId,
        });
      } else if (action === BriefcaseActions.MOVE) {
        briefcaseStore.removeFromBriefcase({
          items,
          folderId: sourceFolderId,
        });
        briefcaseStore.addToBriefcase({
          items,
          // adding non-null assertion here because it's checked above
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          folderId: destinationFolderId!,
        });
      } else {
        LogService.warn(
          `ApiService.postBriefcaseToggleWithId invalid action: '${action}`
        );
      }
      // Update briefcase status of cached items
      // briefcaseStore.refreshAddedToBriefcaseStatusOfItems(
      //   appStore.homePageConfig.featuredProfiles
      // );
      // cashedResult is an array of arrays, so need to nest the call
      // searchStore.search.cachedResults.forEach((searchItems) => {
      //   briefcaseStore.refreshAddedToBriefcaseStatusOfItems(searchItems);
      // });
      return new Promise<boolean>((resolve) => {
        resolve(true);
      });
    }
    return new Promise<boolean>((resolve) => {
      resolve(false);
    });
  }

  // Does not validate arguments, make sure action/sourceFolderId/destinationFolderId are passed as needed
  private static async postBriefcaseToggleWithId(
    briefcaseItemIds: number[],
    action?: string,
    sourceFolderId?: number,
    destinationFolderId?: number
  ): Promise<boolean> {
    const requestBody: BriefcaseToggleRequest = {
      briefcase_item_ids: briefcaseItemIds,
    };
    let success = true;
    if (action !== undefined) {
      requestBody.action = action;
    }
    if (sourceFolderId !== undefined) {
      requestBody.folder_id = sourceFolderId;
    }
    if (destinationFolderId !== undefined) {
      requestBody.dest_folder_id = destinationFolderId;
    }
    return new Promise<boolean>((resolve) => {
      axios
        .post(
          this._getApiEndpoint(VignetteEndpoints.API_BRIEFCASE_TOGGLE),
          requestBody
        )
        .then((response: AxiosResponse) => {
          success = response.status === 200;
          LogService.info(`Response: '${JSON.stringify(response.data || {})}'`);
        })
        .catch(() => {
          // Error logged by axios interceptor
        })
        .then(() => {
          LogService.info("briefcase toggle request is complete.");
          ApiService.refreshBriefcase();
          resolve(success);
        });
    });
  }

  static getBriefcaseFolderDownloadCheck(
    folderId: number,
    datatype = "docx",
    downloadOrder = "alphabetical",
    selectedProfiles: SearchItem[] = [],
    email = false,
    useCustomLogo = false
  ): Promise<BriefcaseFolderDownloadCheckResponse> {
    // Conditionally add params depending on what is needed.
    const params = {
      datatype: datatype,
      order: downloadOrder,
      use_custom_logo: useCustomLogo,
      ...(email && { email: email }),
      ...(selectedProfiles.length > 0 && {
        p: selectedProfiles.map((sp) => `${sp.id}-${sp.recordType}`).join(","),
      }),
    };

    return new Promise<BriefcaseFolderDownloadCheckResponse>((resolve) => {
      let result: BriefcaseFolderDownloadCheckResponse = {
        profile_count: 0,
        eta: 2,
        short: true,
        started: false,
      };
      axios
        .get<BriefcaseFolderDownloadCheckResponse>(
          this._getApiEndpoint(VignetteEndpoints.API_BRIEFCASE_DOWNLOAD_CHECK) +
            folderId,
          { params }
        )
        .then(
          (response: AxiosResponse<BriefcaseFolderDownloadCheckResponse>) => {
            result = response.data;
          }
        )
        .catch(() => {
          // Error logged by axios interceptor
        })
        .then(() => {
          resolve(result);
        });
    });
  }

  static async postBriefcaseFolderAdd(
    folder_name: string
  ): Promise<BriefcaseFolder> {
    const briefcaseStore = useBriefcaseStore();
    const requestBody = {
      folder_name,
    };

    return new Promise<BriefcaseFolder>((resolve, reject) => {
      let result: BriefcaseFolderResponse = {
        success: null,
        folder_id: null,
        detail: "An error occurred, please try again.",
        statusCode: 500,
        dossier_url: "",
      };
      axios
        .post<BriefcaseFolderResponse>(
          this._getApiEndpoint(VignetteEndpoints.API_BRIEFCASE_FOLDER_ADD),
          requestBody
        )
        .then((response: AxiosResponse<BriefcaseFolderResponse>) => {
          result = response.data;

          const newBriefcaseFolder: BriefcaseFolder = {
            id: result.folder_id || 0,
            name: folder_name,
            isDefault: false,
            items: [],
            dossierUrl: result.dossier_url,
          };
          briefcaseStore.addFolder(newBriefcaseFolder);
          resolve(newBriefcaseFolder);
        })
        .catch((error: AxiosError<BriefcaseFolderResponse>) => {
          const response = error.response;

          if (response != null) {
            if (response.status === 403) {
              result = response.data;
              result.statusCode = 403;
            } else if (response.status === 401) {
              result.statusCode = 401;
            }
          }
          reject(error.response);
        })
        .then(() => {
          LogService.info("postBriefcaseFolderAdd request is complete.");
        });
    });
  }

  static async postBriefcaseFolderDelete(
    folder_id: number
  ): Promise<BriefcaseFolderResponse> {
    const briefcaseStore = useBriefcaseStore();
    const requestBody = {
      folder_id,
    };

    return new Promise<BriefcaseFolderResponse>((resolve) => {
      let result: BriefcaseFolderResponse = {
        success: null,
        folder_id: null,
        detail: "An error occurred, please try again.",
        statusCode: 500,
        dossier_url: "",
      };
      axios
        .post<BriefcaseFolderResponse>(
          this._getApiEndpoint(VignetteEndpoints.API_BRIEFCASE_FOLDER_DELETE),
          requestBody
        )
        .then((response: AxiosResponse<BriefcaseFolderResponse>) => {
          result = response.data;
          result.statusCode = 200;

          const folderToBeDeleted: BriefcaseFolder | undefined =
            briefcaseStore.folders[folder_id];

          // Move any items to default folder
          if (
            folderToBeDeleted != undefined &&
            folderToBeDeleted.items.length > 0 &&
            folderToBeDeleted.items.length > 0
          ) {
            const items: BriefcaseItem[] = folderToBeDeleted.items;
            briefcaseStore.removeFromBriefcase({
              items: items,
              folderId: folder_id,
            });
          }
          briefcaseStore.removeFolder(folder_id);
        })
        .catch((error: AxiosError<BriefcaseFolderResponse>) => {
          const response = error.response;

          if (response != null) {
            if (response.status === 403) {
              result = response.data;
              result.statusCode = 403;
            } else if (response.status === 401) {
              result.statusCode = 401;
            }
          }
        })
        .then(() => {
          LogService.info("postBriefcaseFolderDelete request is complete.");
          resolve(result);
        });
    });
  }

  static async getSession(): Promise<void> {
    const briefcaseStore = useBriefcaseStore();
    const { setLightweightBriefcaseData } = briefcaseStore;
    return new Promise<void>((resolve) => {
      axios
        .get<SessionResponse>(
          this._getApiEndpoint(VignetteEndpoints.API_SESSION)
        )
        .then((response: AxiosResponse<SessionResponse>) => {
          ApiService.updateUserSession(response.data, true);
          setLightweightBriefcaseData(
            response.data.briefcase.items,
            response.data.briefcase.folders.map(
              this.parseBriefcaseResponseFolder
            )
          );
        })
        .catch((error: AxiosError<SessionResponse>) => {
          const response = error.response;

          // Is logged in but doesn't have Vignette access
          if (response != null && response.status === 403) {
            ApiService.updateUserSession(response.data, false);
          }
        })
        .then(() => {
          LogService.info("session updated.");
          resolve();
        });
    });
  }

  private static updateUserSession(
    response: SessionResponse,
    hasVignetteAccess: boolean
  ): void {
    const sessionStore = useSessionStore();
    const user = this.parseSessionResponse(response);
    sessionStore.$patch({
      user: user,
      hasVignetteAccess: hasVignetteAccess,
    });
  }

  private static parseSessionResponse(response: SessionResponse): User {
    return {
      firstName: response.first_name || "",
      lastName: response.last_name || "",
      // FIXME: make sure invalid string doesn't get saved like valid enum
      userType: response.user_type as UserType,
      username: response.username || "",
      email: response.email || "",
      accountName: response.account_name || "",
      salesforceContactId: response.salesforce_contact_id || "",
      salesforceId: response.salesforce_id || "",
      salesforceAccessString: this.parseSalesforceAccessString(
        response.salesforce_access_string || ""
      ),
      subscriptionModules: response.subscription_modules || [],
      accountId: response.account_id || "",
      accountExpiration: this.getDateObject(response.expiration || ""),
      accountExpirationStr: this.parseIsoDate(response.expiration || ""),
      accountOrganization: {
        salesforceAccountId:
          response.account_organization.salesforce_account_id || "",
        salesforceAccountName:
          response.account_organization.salesforce_account_name || "",
        hasCustomLogo: response.account_organization.has_custom_logo || false,
      },
      pcTemplates: response.pc_templates || [],
      almanacExportCount: response.almanac_export_count,
      isSuperUser: response.is_superuser || false,
      isStaff: response.is_staff || false,
      hidePayWalledProfilesPreference:
        response.hide_paywalled_profiles_preference || false,
    };
  }

  // TODO: make sure validate input, test for null/""/";"/"valid:2020-01-01"/"valid:2020-01-01;"
  private static parseSalesforceAccessString(
    accessString: string
  ): SalesforcePermission[] {
    const salesforcePermissions: string[] = accessString.split(";");
    const result: SalesforcePermission[] = [];
    for (const permissionTuple of salesforcePermissions) {
      const permission: string[] = permissionTuple.split(":");
      if (permission[0] && permission[1]) {
        result.push({ codename: permission[0], expiration: permission[1] });
      }
    }
    return result;
  }

  /**
   * Get the app config from the API. This api request should not
   * generate any errors unless they are 500 level errors. This is not
   * an auth guarded request.
   */
  static async getAppConfig(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      axios
        .get<AppConfigResponse>(
          this._getApiEndpoint(VignetteEndpoints.API_APP_CONFIG)
        )
        .then((response: AxiosResponse<AppConfigResponse>) => {
          ApiService.updateAppConfig(response.data);
        })
        .catch((error: AxiosError<AppConfigResponse>) => {
          const response = error.response;

          if (response?.status) {
            // Log errors - Network error / CORS issue / backend error
            LogService.addBreadcrumb({
              data: error.response?.data,
              category: "api",
              message: `response is ${response?.status || "null"}`,
              level: "error",
              type: "getAppConfig",
            });
            reject(error);
          }
        })
        .then(() => {
          LogService.info("getAppConfig request is complete");
          resolve();
        });
    });
  }

  private static updateAppConfig(response: AppConfigResponse): void {
    const appStore = useAppStore();
    const {
      lookupTables,
      nestedLookupTables,
      unlockedProfiles,
      stateLegJobCategories,
      legislativeChambers,
      districts,
      districtMap,
      constants,
    } = storeToRefs(appStore);
    if (
      response.lists == null ||
      response.lists.subscription_modules == null ||
      response.lists.subscription_modules.length === 0
    ) {
      LogService.warn(
        `An invalid SubscriptionModulesList has been returned from appConfig endpoint`
      );
    }

    constants.value = parseResponse<AppConfigConstants>(
      response.lists.constants
    );

    // Initialize from response, TODO: make sure we handle null cases
    lookupTables.value.subscriptionModules =
      response.lists.subscription_modules.map<SubscriptionModule>(
        (module: SubscriptionModuleResponse) => ({
          id: module.id,
          name: module.name,
          abbreviation: module.abbreviation,
        })
      );
    lookupTables.value.editorialGroups =
      response.lists.editorial_groups.map<LookupTableItem>((item) =>
        ApiService.mapAppConfigNumberResponseToLookupTableItem(item)
      );
    lookupTables.value.states = response.lists.states;
    lookupTables.value.countries = response.lists.countries;
    lookupTables.value.positionStatuses = response.lists.position_statuses;
    lookupTables.value.genders = response.lists.genders;
    lookupTables.value.politicalParties = response.lists.political_parties;
    lookupTables.value.maritalStatuses = response.lists.marital_statuses;
    lookupTables.value.ethnicities = response.lists.ethnicities;
    lookupTables.value.religions = response.lists.religions;
    // lookupTables.industries = response.lists.industries;
    lookupTables.value.jobCategories = response.lists.leg_job_categories.map(
      (el, idx) => {
        return {
          name: el,
          id: idx.toString(),
        };
      }
    );
    lookupTables.value.jobCategoriesFederal =
      response.lists.agency_job_categories.map((el, idx) => {
        return {
          name: el,
          id: idx.toString(),
        };
      });
    lookupTables.value.degrees = response.lists.degrees;
    lookupTables.value.focusAreas = response.lists.focus_areas.map((f) => ({
      id: f,
      name: f,
    }));
    districts.value = response.lists.districts;
    districtMap.value.US_HOUSE = response.lists.districts.filter(
      (d) => d.legchamberid === legislativeChambers.value.US_HOUSE
    );
    districtMap.value.STATE_HOUSE = response.lists.districts.filter(
      (d) => d.legchamberid === legislativeChambers.value.STATE_HOUSE
    );
    districtMap.value.STATE_SENATE = response.lists.districts.filter(
      (d) => d.legchamberid === legislativeChambers.value.STATE_SENATE
    );
    districtMap.value.STATE_COMBINED = [
      ...districtMap.value.STATE_HOUSE,
      ...districtMap.value.STATE_SENATE,
    ];
    lookupTables.value.appointments = response.lists.appointments.map((el) => {
      return {
        name: el[1],
        id: el[0].toString(),
      };
    });

    nestedLookupTables.value.profileTypes =
      response.lists.profile_types.map<NestedLookupTableItem>(
        (item: AppConfigNestedNumberResponse) =>
          this.mapAppConfigNestedNumberResponseToNestedLookupTableItem(item)
      );
    nestedLookupTables.value.keyPolicyAreas =
      response.lists.key_policy_areas.map<NestedLookupTableItem>(
        (item: AppConfigNestedNumberResponse) =>
          this.mapAppConfigNestedNumberResponseToNestedLookupTableItem(item)
      );
    nestedLookupTables.value.industries =
      response.lists.industries.map<NestedLookupTableItem>(
        (item: AppConfigNestedNumberResponse) =>
          this.mapAppConfigNestedNumberResponseToNestedLookupTableItem(item)
      );
    nestedLookupTables.value.committees =
      response.lists.committees.map<NestedLookupTableItem>(
        (item: AppConfigNestedNumberResponse) =>
          this.mapAppConfigNestedNumberResponseToNestedLookupTableItem(item)
      );
    nestedLookupTables.value.caucuses =
      response.lists.caucuses.map<NestedLookupTableItem>(
        (item: AppConfigNestedNumberResponse) =>
          this.mapAppConfigNestedNumberResponseToNestedLookupTableItem(item)
      );
    nestedLookupTables.value.agencies =
      response.lists.agencies.map<NestedLookupTableItem>(
        (item: AppConfigNestedNumberResponse) =>
          this.mapAppConfigNestedNumberResponseToNestedLookupTableItem(item)
      );
    nestedLookupTables.value.specialInterestGroups =
      response.lists.special_interest_groups.map<NestedLookupTableItem>(
        (item: AppConfigNestedNumberResponse) =>
          this.mapAppConfigNestedNumberResponseToNestedLookupTableItem(item)
      );

    for (const st in response.lists.state_committees) {
      if (
        !Object.prototype.hasOwnProperty.call(
          response.lists.state_committees,
          st
        )
      )
        continue;
      const bucket: ParsedResponse<StateCommitteeBucketResponse> =
        parseResponse(response.lists.state_committees[st]);
      appStore.stateCommitteesMap[st] = bucket;
      type CommitteeNode = NestedLookupTableItem;
      type FlatCommittee = { id: string; name: string };
      const flatten = (nodes: CommitteeNode[]): FlatCommittee[] => {
        const out: FlatCommittee[] = [];
        for (const x of nodes) {
          if (!x || !x.id || !x.name) continue;
          out.push({ id: String(x.id), name: String(x.name) });
          const children = (
            x as unknown as {
              children?: { id: string | number; name: string }[];
            }
          ).children;
          if (Array.isArray(children) && children.length) {
            out.push(...flatten(children as unknown as CommitteeNode[]));
          }
        }
        return out;
      };
      const chambers: CommitteeNode[] = [
        ...(bucket.house ?? []),
        ...(bucket.senate ?? []),
        ...(bucket.joint ?? []),
      ];
      const flat = chambers.reduce<FlatCommittee[]>(
        (acc, n) => acc.concat(flatten([n])),
        []
      );
      for (const item of flat)
        appStore.stateCommitteesFlatLookupMap.set(item.id, item);
    }

    // do flat lookup tables
    const getFlat = (
      list: NestedLookupTableItem[],
      flattened: LookupTableItem[]
    ): LookupTableItem[] => {
      list.forEach((item) => {
        // need to use Object.assign because removing the 'children' key on each object will save space
        // but we don't want to alter the original object
        const l = Object.assign({}, item);
        if (l.children && l.children.length > 0) {
          const flatChildren = getFlat(l.children, flattened);
          delete l.children;
          flattened.concat(flatChildren);
        }
        flattened.push(l);
      });
      return flattened;
    };
    Object.values(NestedLookupTablesKeys).forEach((value) => {
      lookupTables.value[(value + "Flat") as LookupTablesKeys] = getFlat(
        nestedLookupTables.value[value],
        []
      );
    });

    stateLegJobCategories.value = response.lists.state_leg_job_categories;

    // Compute generated fields
    appStore.setLookupTablesGeneratedFields();
    unlockedProfiles.value = response.lists.unlocked_profiles;

    const { searchSuggestions } = appStore;
    this._parseSearchSuggestions(
      searchSuggestions,
      response.lists.search_suggestions
    );
  }

  private static _parseSearchSuggestions(
    searchSuggestions: SearchSuggestions,
    response: SearchSuggestionResponse
  ): void {
    for (const k in response) {
      const key: SearchRouteTypes = k as SearchRouteTypes;
      if (!response[key]) continue;
      const responseSuggestions = response[key];
      if (responseSuggestions.length === 0 || !responseSuggestions) continue;
      const suggestion = responseSuggestions.map((s) => `"${s}"`).join(", ");
      searchSuggestions[key] = `Examples: ${suggestion}`;
    }
  }

  private static mapAppConfigNumberResponseToLookupTableItem(
    response: AppConfigNumberResponse
  ): LookupTableItem {
    const lookupTableItem: LookupTableItem = {
      id: String(response.id),
      name: response.name,
    };

    if (response.description)
      lookupTableItem.description = response.description;
    return lookupTableItem;
  }

  private static mapAppConfigNestedNumberResponseToNestedLookupTableItem(
    response: AppConfigNestedNumberResponse
  ): NestedLookupTableItem {
    const result: NestedLookupTableItem =
      this.mapAppConfigNumberResponseToLookupTableItem(response);
    if (response.children != null && response.children.length > 0) {
      result.children = response.children.map<LookupTableItem>((item) =>
        this.mapAppConfigNestedNumberResponseToNestedLookupTableItem(item)
      );
    }
    return result;
  }

  static async postUserContact(
    message: string,
    request_custom_logo: boolean
  ): Promise<UserContactResponse> {
    const requestBody = {
      message,
      request_custom_logo,
    };

    return new Promise<UserContactResponse>((resolve) => {
      let result: UserContactResponse = {
        status_msg: "",
        error_message: "An error occurred, please try again.",
        statusCode: 500,
      };
      axios
        .post<UserContactResponse>(
          this._getApiEndpoint(VignetteEndpoints.API_REQUEST_CUSTOM_LOGO),
          requestBody
        )
        .then((response: AxiosResponse<UserContactResponse>) => {
          result = response.data;
          result.statusCode = 200;
        })
        .catch((error: AxiosError<UserContactResponse>) => {
          const response = error.response;

          if (response != null) {
            if (response.status === 403) {
              result = response.data;
              result.statusCode = 403;
            }
            // Not logged in
            else if (response.status === 401) {
              result.statusCode = 401;
            }
            // else - result.statusCode remains 500 (set during initialization)
          }
        })
        .then(() => {
          LogService.info("postUserContact request is complete.");
          resolve(result);
        });
    });
  }

  /**
   * Calls the backend to estimate ETA and profile count for bulk dossier download from search params.
   * Mirrors backend SearchDossierDownloadCheck endpoint.
   * @param params - Query parameters (Universal/Vignette search params + datatype, use_custom_logo, etc)
   */
  static async getBulkDossierDownloadCheck(
    params: URLSearchParams
  ): Promise<Result<BulkDossierDownloadCheckResponse, NonSuccessResponse>> {
    // Endpoint assumed: /api/vignette/search_dossier_download_check/
    const endpoint = this._getApiEndpoint(
      VignetteEndpoints.API_BULK_DOSSIER_DOWNLOAD_CHECK,
      "",
      params
    );
    try {
      const response = await axios.get<BulkDossierDownloadCheckResponse>(
        endpoint
      );
      return {
        ok: true,
        data: response.data,
        error: null,
      };
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        const errorData = error?.response?.data;
        if (errorData) {
          const message = errorData.error_message ?? errorData.detail;
          return {
            ok: false,
            data: null,
            error: new NonSuccessResponse(message, ""),
          };
        }
      }
      // catch all
      const message =
        "Could not retrieve dossier download info, try again or please contact your Dedicated Advisor.";
      return {
        ok: false,
        data: null,
        error: new NonSuccessResponse(message, ""),
      };
    }
  }

  /**
   * Calls the backend to check processing time for dossiers and/or trigger email from search params.
   * Mirrors backend SearchDossierDownload endpoint.
   * @param params - Query parameters (Universal/Vignette search params + datatype, use_custom_logo, doc_type, doc_name, email, etc)
   */
  static async getBulkDossierEmail(
    params: TypedURLSearchParams
  ): Promise<Result<BulkDossierDownloadCheckResponse, NonSuccessResponse>> {
    // Endpoint assumed: /api/vignette/search_dossier_download/
    const endpoint =
      this._getApiEndpoint(
        VignetteEndpoints.API_BULK_DOSSIER_DOWNLOAD_CHECK,
        "",
        params
      ) + "&email=true";
    try {
      const response = await axios.get(endpoint);
      return {
        ok: true,
        data: response.data,
        error: null,
      };
    } catch (error) {
      return {
        ok: false,
        data: null,
        error: new NonSuccessResponse(
          "Could not retrieve dossier download info, try again or please contact your Dedicated Advisor.",
          ""
        ),
      };
    }
  }
}
